import {
	useEffect,
	useMemo,
	useState,
	type ChangeEvent,
	type FormEvent,
} from 'react'

import { Field } from '#app/components/form/field.tsx'
import { SelectField } from '#app/components/form/select-field.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'
import {
	TEST_ROUTINE_COPY,
	ROUTINE_TEST_NAME_OPTIONS,
	TEST_FREQUENCY_OPTIONS,
} from '#app/routes/mocra+/_form+/components/test-routines/constants.ts'
import {
	createTestRoutineDraft,
	sanitizeTestRoutineDraft,
	type TestRoutineDraft,
} from '#app/routes/mocra+/_form+/components/test-routines/types.ts'

type Mode = 'add' | 'edit'

type DraftErrors = Partial<Pick<TestRoutineDraft, 'nameOther'>>

type Props = {
	readonly mode: Mode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: TestRoutineDraft) => void
	readonly initialValue?: TestRoutineDraft
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

export function TestRoutineOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<TestRoutineDraft>(
		initialValue ? { ...initialValue } : createTestRoutineDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})

	useEffect(() => {
		if (open) {
			setDraft(initialValue ? { ...initialValue } : createTestRoutineDraft())
			setErrors({})
		} else if (!open) {
			onClosed?.()
		}
	}, [initialValue, onClosed, open])

	const descriptionCopy = TEST_ROUTINE_COPY.description
	const titleCopy =
		mode === 'add' ? TEST_ROUTINE_COPY.title.add : TEST_ROUTINE_COPY.title.edit
	const submitCopy =
		mode === 'add'
			? TEST_ROUTINE_COPY.submit.add
			: TEST_ROUTINE_COPY.submit.edit

	const nameOptions = useMemo(() => ROUTINE_TEST_NAME_OPTIONS, [])
	const frequencyOptions = useMemo(() => TEST_FREQUENCY_OPTIONS, [])
	const showCustomName = draft.name === 'other'

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitizeTestRoutineDraft(draft)
		const nextErrors: DraftErrors = {}

		if (sanitized.name === 'other' && !sanitized.nameOther) {
			nextErrors.nameOther = 'Enter the test name.'
		}

		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors)
			return
		}

		onSubmit(sanitized)
		onOpenChange(false)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={descriptionCopy}
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button type="submit" form="test-routine-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<form id="test-routine-overlay-form" onSubmit={handleSubmit}>
				<SelectField
					labelProps={{ children: TEST_ROUTINE_COPY.fields.nameLabel }}
					selectProps={{
						value: draft.name,
						onChange: (event: ChangeEvent<HTMLSelectElement>) =>
							setDraft((prev) => ({
								...prev,
								name: event.target.value as TestRoutineDraft['name'],
							})),
					}}
					options={nameOptions}
					descriptionProps={{
						children: TEST_ROUTINE_COPY.fields.nameDescription,
					}}
				/>
				{showCustomName ? (
					<Field
						labelProps={{ children: TEST_ROUTINE_COPY.fields.nameOtherLabel }}
						inputProps={{
							value: draft.nameOther,
							onChange: (event: ChangeEvent<HTMLInputElement>) =>
								setDraft((prev) => ({
									...prev,
									nameOther: event.target.value,
								})),
							type: 'text',
						}}
						errors={errors.nameOther ? [errors.nameOther] : undefined}
					/>
				) : null}
				<SelectField
					labelProps={{ children: TEST_ROUTINE_COPY.fields.frequencyLabel }}
					selectProps={{
						value: draft.frequency,
						onChange: (event: ChangeEvent<HTMLSelectElement>) =>
							setDraft((prev) => ({
								...prev,
								frequency: event.target.value as TestRoutineDraft['frequency'],
							})),
					}}
					options={frequencyOptions}
				/>
			</form>
		</ResponsiveOverlay>
	)
}
